import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';

// ─── Theme (light / dark) for the whole /environmental area ───────────────────
export type EnvTheme = 'light' | 'dark';
const THEME_KEY = 'env.theme';

const ThemeCtx = createContext<{ theme: EnvTheme; toggle: () => void }>({ theme: 'light', toggle: () => {} });
export const useEnvTheme = () => useContext(ThemeCtx);

function initialTheme(): EnvTheme {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* storage blocked */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** White (or dark) wrapper for everything under /environmental — no site navbar. */
export default function EnvLayout() {
  const [theme, setTheme] = useState<EnvTheme>(initialTheme);

  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  // The site has its own day/night class; park it while this area is open.
  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains('theme-light');
    html.classList.remove('theme-light');
    return () => { if (wasLight) html.classList.add('theme-light'); };
  }, []);

  // Keep the page background (overscroll area) in step with the theme.
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = theme === 'dark' ? '#020617' : '#ffffff';
    return () => { document.body.style.backgroundColor = prev; };
  }, [theme]);

  return (
    <ThemeCtx.Provider value={{ theme, toggle: () => setTheme(t => (t === 'dark' ? 'light' : 'dark')) }}>
      <div className={`relative min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100 ${theme === 'dark' ? 'env-dark' : ''}`}>
        <Outlet />
      </div>
    </ThemeCtx.Provider>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useEnvTheme();
  const dark = theme === 'dark';
  return (
    <button onClick={toggle} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={dark ? 'Light mode' : 'Dark mode'}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100">
      {dark ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="4" />
          <path strokeLinecap="round" d="M12 2v2m0 16v2M2 12h2m16 0h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
        </svg>
      )}
    </button>
  );
}

export function Spinner() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
    </div>
  );
}

/** Top bar + body used by every game page. `right` holds game-specific actions. */
export function GameShell({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 px-3 backdrop-blur sm:gap-3 sm:px-4">
        <Link to="/environmental"
              className="shrink-0 rounded-xl px-2.5 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
          ← Games
        </Link>
        <img src="/logo.png" alt="" className="hidden h-8 w-8 object-contain sm:block" />
        <h1 className="min-w-0 flex-1 truncate text-base font-bold">{title}</h1>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {right}
          <ThemeToggle />
        </div>
      </header>
      <div className="flex-1">{children}</div>
    </div>
  );
}

/** Puts data-theme on the page's <html> tag (replacing one if present). */
function withTheme(html: string, theme: EnvTheme): string {
  return html.replace(/<html\b([^>]*)>/i, (_m, attrs: string) => {
    const rest = attrs.replace(/\sdata-theme=("[^"]*"|'[^']*'|[^\s>]+)/i, '');
    return `<html${rest} data-theme="${theme}">`;
  });
}

/** Full-height iframe under the GameShell bar (3.5rem); mirrors the area's theme into the page. */
export function GameFrame({ html, title }: { html: string; title: string }) {
  const { theme } = useEnvTheme();
  const frame = useRef<HTMLIFrameElement>(null);

  // The starting theme is baked into the document once; later toggles only flip the attribute,
  // so switching light/dark never reloads (and resets) the game.
  const [startTheme] = useState(theme);
  const doc = useMemo(() => withTheme(html, startTheme), [html, startTheme]);

  const syncTheme = () => {
    try { frame.current?.contentDocument?.documentElement.setAttribute('data-theme', theme); } catch { /* cross-origin */ }
  };
  useEffect(syncTheme, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <iframe
      ref={frame}
      title={title}
      srcDoc={doc}
      onLoad={syncTheme}
      className="block w-full border-0"
      style={{ height: 'calc(100dvh - 3.5rem)', minHeight: 520 }}
    />
  );
}

/** Centered dialog; closes on backdrop click or Escape. */
export function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}
